import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithCustomToken, signOut, UserCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, functions, db } from '@/lib/firebase';
import i18n from '@/i18n';

interface AuthContextType {
    user: User | null;
    guestData: any | null;
    loading: boolean;
    signInWithCode: (code: string) => Promise<UserCredential | undefined>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    guestData: null,
    loading: true,
    signInWithCode: async () => undefined,
    logout: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [guestData, setGuestData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            setUser(u);
            
            if (u) {
                try {
                    const guestRef = doc(db, 'guests', u.uid);
                    const guestSnap = await getDoc(guestRef);
                    if (guestSnap.exists()) {
                        const data = guestSnap.data();
                        setGuestData(data);
                        // Automatically switch to Spanish for guests from Spain, otherwise reset to English
                        if (data.country === 'ES') {
                            i18n.changeLanguage('es');
                        } else {
                            i18n.changeLanguage('en');
                        }
                    } else {
                        setGuestData(null);
                    }
                } catch (error) {
                    console.error("Error fetching guest data in AuthContext:", error);
                    setGuestData(null);
                }
            } else {
                setGuestData(null);
            }
            
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const signInWithCode = async (code: string) => {
        const createGuestSession = httpsCallable(functions, 'createGuestSession');
        const result = await createGuestSession({ inviteCode: code });
        const { token } = result.data as { token: string };
        return await signInWithCustomToken(auth, token);
    };

    const logout = async () => {
        await signOut(auth);
    };

    return (
        <AuthContext.Provider value={{ user, guestData, loading, signInWithCode, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
